<script setup lang="ts">
import ArrayTable from "@/components/ArrayTable.vue"
import Loading from "@/components/Loading.vue"
import { usePlaygroundStore } from "@/stores/playground"
import { useEngineModel } from "@/utils/useEngineModel"
import { EngineError } from "@/utils/useEngineModel/error"
import { useSnackbar } from "@/utils/useSnackbar"
import workerImport from "@/workers/parseWorker?worker&url"
import { KTextInput } from "@kaleidoscope-solutions/ks-design-system"
import { CloseRingIcon } from "@kaleidoscope-solutions/ks-icons/vue/light"
import { useWebWorker, watchDebounced } from "@vueuse/core"

import saveAs from "file-saver"
import { ref, useTemplateRef, watch } from "vue"

const model = useEngineModel()

const DUMMY_UUID = "00000000-0000-4000-0000-000000000000"

const worker = useWebWorker(workerImport, { type: "module" })

const loading = ref<boolean>(true)
const errorMessage = ref<string>()

const tableData = ref<string[][]>()

const playgroundStore = usePlaygroundStore()

playgroundStore.loadState()

const selectedText = ref("")
const textInputRef = useTemplateRef("textInputRef")

const activeFormula = ref<string>()

const { addSnackbar } = useSnackbar()

function getSelectedText() {
  selectedText.value = ""
  if (textInputRef.value) {
    const input = textInputRef.value.$el.querySelector("input")
    selectedText.value = input.value.substring(input.selectionStart, input.selectionEnd)
  }
}

function handleError(error: Error | EngineError | unknown) {
  if (error instanceof EngineError) {
    errorMessage.value = `${error.messageWithoutCode}`
  } else {
    errorMessage.value = `${error}`
  }
  tableData.value = []
  playgroundStore.resetLocalStorage()

  loading.value = false
}

async function runFormula(formula: string) {
  if (!formula) {
    tableData.value = []
    return
  }

  // Only run if the formula is different
  if (formula === activeFormula.value) {
    return
  }

  loading.value = true

  activeFormula.value = formula

  try {
    errorMessage.value = undefined

    const playground = await model.api.playground(playgroundStore.chartWidgetId ?? DUMMY_UUID, formula)

    worker.post({ childData: playground.childData, data: playground.data, formula })
  } catch (error) {
    handleError(error)
  }
}

watch(
  () => worker.data.value,
  () => {
    try {
      const { formula, data, error } = worker.data.value

      if (error) {
        throw new Error(error)
      }

      if (activeFormula.value !== formula) {
        throw new Error("Formula mismatch")
      }

      // This will only save if it correctly renders
      if (activeFormula.value === playgroundStore.formula) {
        playgroundStore.saveState()
      }

      tableData.value = data

      loading.value = false
    } catch (error) {
      handleError(error)
    }
  },
)

watch(
  () => playgroundStore.formula,
  () => {
    if (playgroundStore.formula) {
      selectedText.value = ""
      loading.value = true
    } else {
      tableData.value = []
      loading.value = false
      errorMessage.value = undefined
      selectedText.value = ""
      activeFormula.value = undefined
    }
  },
)

watchDebounced(
  () => playgroundStore.formula ?? "",
  () => playgroundStore.formula ? runFormula(playgroundStore.formula) : runFormula(""),
  { debounce: 1000, immediate: true },
)

watch(
  selectedText,
  () => selectedText.value ? runFormula(selectedText.value) : runFormula(playgroundStore.formula || "")
)

function tableToCSV() {
  if (!tableData.value) {
    return null
  }

  // Export so it will auto format in Excel
  return tableData.value
    .map((row) =>
      row
        .map((cell) => `"${cell.replace(/"/g, "\"\"")}"`) // Escape double quotes
        .join("\t") // Use tab as delimiter
    )
    .join("\n") // Join rows with newline
}

function copyTable() {
  const csvText = tableToCSV()
  if (csvText) {
    try {
      navigator.clipboard.writeText(csvText)
    } catch (error) {
      console.error(error)
      addSnackbar({
        type: "error",
        message: "Failed to copy table to clipboard",
      })
    }

    addSnackbar({
      type: "success",
      message: "Table copied to clipboard",
    })
  }
}

function saveTable() {
  const csvText = tableToCSV()
  if (csvText) {
    const blob = new Blob([csvText], { type: "text/csv" })
    const name = `${playgroundStore.chartWidgetId || "table"}.csv`
    try {
      saveAs(blob, name)
      addSnackbar({
        type: "success",
        message: `Table saved successfully as \n${name}`,
      })
    } catch (error) {
      console.error(error)
      addSnackbar({
        type: "error",
        message: "Failed to save table",
      })
    }
  }
}
</script>

<template>
  <div class="flex flex-col overflow-hidden gap-4 p-1 h-full">
    <div class="p-4 shadow flex flex-col gap-2 rounded-lg bg-white">
      <div class="flex gap-2 items-center">
        <KTextInput
          v-model="playgroundStore.chartWidgetId"
          placeholder="Chart Widget ID"
          class="font-mono"
        />
        <button
          title="Clear Chart Widget ID"
          @click="playgroundStore.chartWidgetId = ''"
        >
          <CloseRingIcon class="size-7 text-violet-700" />
        </button>
      </div>
      <div class="flex gap-2 items-center">
        <KTextInput
          ref="textInputRef"
          v-model="playgroundStore.formula"
          placeholder="Formula"
          class="font-mono"
          @mouseup="getSelectedText"
        />
        <button
          title="Clear formula"
          @click="playgroundStore.formula = ''"
        >
          <CloseRingIcon class="size-7 text-violet-700" />
        </button>
      </div>
      <div
        v-if="selectedText"
        class="flex gap-2 items-center"
      >
        <span>Running partial formula: </span>
        <button @click="selectedText = ''">
          <CloseRingIcon class="size-4" />
        </button>
        <div

          class="rounded-lg bg-gray-200 p-2 font-mono"
        >
          {{ selectedText }}
        </div>
      </div>
      <div
        v-if="errorMessage"
        class="rounded-lg bg-red-200 p-4"
      >
        {{ errorMessage }}
      </div>
    </div>
    <div
      v-if="!loading && activeFormula && !errorMessage && tableData?.length"
      :key="activeFormula + tableData.length"
      class="overflow-auto flex-1"
    >
      <div class="flex gap-1 mb-4">
        <KButton
          variety="primary"
          color="violet"
          class="shadow-none"
          @click="copyTable()"
        >
          Copy Table
        </KButton>

        <KButton
          variety="primary"
          color="violet"
          class="shadow-none ms-2"
          @click="saveTable()"
        >
          Save Table
        </KButton>
      </div>

      <div class="color-grey-200 text-sm mb-2">
        Rows: {{ new Intl.NumberFormat().format(tableData.length - 1) }}
      </div>
      <ArrayTable
        :table-data="tableData"
        :read-only="false"
      />
    </div>
    <div
      v-if="loading && activeFormula"
      class="fixed inset-20 z-20 grid place-content-center gap-4"
    >
      <Loading class="h-40 w-40" />
      <div class="animate-pulse">
        Running Formula ...
      </div>
    </div>
  </div>
</template>
